import React from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import UploadArea from "./UploadArea";
import PromptField from "./PromptField";
import DurationAspectRow from "./DurationAspectRow";
import VoiceoverSection from "./VoiceoverSection";
import MusicAudioSection from "./MusicAudioSection";
import ScenePlanEditor from "./ScenePlanEditor";
import { klingO3ModeLabel } from "../modelProfiles";
import { getGenerationMode, getGenerationModeRules } from "../studioGenerationMode";
import { formHasScenePlan } from "../scenePlan";

export const KLING_O3_VARIANTS = [
  { id: "kling-o3-standard", label: "kling-o3/standard", api: "kling-o3/standard" },
  { id: "kling-o3-pro", label: "kling-o3/pro", api: "kling-o3/pro" },
];

const KLING_ASPECT = ["16:9", "9:16", "1:1"];

const MODE_LABEL_KEYS = {
  "text-to-video": "videoStudioCatTextToVideo",
  "image-to-video": "videoStudioCatImageToVideo",
  "video-to-video": "videoStudioCatVideoToVideo",
  "music-to-video": "videoStudioCatMusicToVideo",
};

export default function KlingO3FormPanel({
  form,
  onChange,
  lang,
  categoryId,
  modelId,
  onGenerateScene,
  generatingScenes,
  sceneStatuses,
}) {
  const set = (patch) => onChange({ ...form, ...patch });
  const generationMode = getGenerationMode(categoryId);
  const rules = getGenerationModeRules(generationMode);
  const modeLabel =
    lang?.[MODE_LABEL_KEYS[generationMode]] || klingO3ModeLabel(generationMode);

  const variant =
    KLING_O3_VARIANTS.find((v) => v.id === form.model) || KLING_O3_VARIANTS[0];

  return (
    <Stack spacing={2.5}>
      <Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={variant.id}
          onChange={(_, v) => v && set({ model: v })}
          sx={{ flexWrap: "wrap" }}
        >
          {KLING_O3_VARIANTS.map((v) => (
            <ToggleButton
              key={v.id}
              value={v.id}
              sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600 }}
            >
              {v.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {lang?.videoStudioModeLabel || "Mode"}:{" "}
        <Box component="span" fontWeight={700} color="text.primary">
          {modeLabel}
        </Box>
      </Typography>

      {/* Uploads first so @-mentions have assets available while typing the prompt. */}
      {rules.showImages && (
        <UploadArea
          lang={lang}
          enableVoiceClip
          label={
            generationMode === "video-to-video"
              ? lang?.videoStudioCharacterImage || "Character / avatar"
              : lang?.videoStudioInputImage || "Input image"
          }
          hint={
            generationMode === "video-to-video"
              ? lang?.videoStudioCharacterImageHint ||
                "Portrait photo — tap the mic below to attach this character’s voice"
              : lang?.videoStudioKlingImageUrlsHint ||
                "JPEG, PNG, WebP — tap the mic below each image to attach their voice"
          }
          files={form.imageUrlUploads || []}
          onChange={(imageUrlUploads) => set({ imageUrlUploads })}
          maxFiles={generationMode === "video-to-video" ? 1 : 2}
        />
      )}

      {rules.showReferenceImages && (
        <UploadArea
          lang={lang}
          label={lang?.videoStudioProductRefImage || "Product reference"}
          hint={
            lang?.videoStudioProductRefImageHint ||
            "Packshot or label — guides the product shown in the ad (JPEG/PNG, max 10MB)"
          }
          files={form.referenceImageUploads || []}
          onChange={(referenceImageUploads) => set({ referenceImageUploads })}
          maxFiles={2}
        />
      )}

      {rules.showVideos && (
        <UploadArea
          lang={lang}
          kind="video"
          accept="video/mp4,video/webm,video/quicktime"
          maxBytes={50 * 1024 * 1024}
          label={lang?.videoStudioInputVideo || "Reference video"}
          hint={lang?.videoStudioRefVideosHint || "MP4, WebM, MOV — max 50MB"}
          files={form.referenceVideoUploads || []}
          onChange={(referenceVideoUploads) => set({ referenceVideoUploads })}
          maxFiles={1}
        />
      )}

      {rules.showPrompt && (
        <PromptField
          form={form}
          onChange={onChange}
          lang={lang}
          textFieldProps={{ minRows: 4 }}
        />
      )}

      {rules.showMusic && <MusicAudioSection form={form} onChange={onChange} lang={lang} />}

      {rules.showVoiceover && (
        <VoiceoverSection form={form} onChange={onChange} lang={lang} modelId={modelId || form.model} />
      )}

      <DurationAspectRow
        form={form}
        onChange={onChange}
        lang={lang}
        modelId={modelId || form.model}
        aspectOptions={KLING_ASPECT}
      />

      {formHasScenePlan(form) ? (
        <ScenePlanEditor
          form={form}
          onChange={onChange}
          lang={lang}
          onGenerateScene={onGenerateScene}
          generatingScenes={generatingScenes}
          sceneStatuses={sceneStatuses}
        />
      ) : null}

      {!rules.showMusic && (
        <FormControlLabel
          control={
            <Switch
              checked={form.sound !== false}
              onChange={(e) => set({ sound: e.target.checked })}
            />
          }
          label={lang?.videoStudioSound || "Sound (native voiceover)"}
        />
      )}
    </Stack>
  );
}
